// Utility functions for opening links in Bitrix24 environment
import { getB24 } from '../context/BitrixContext';

/**
 * Checks if a URL should open in the Bitrix24 slider.
 * - Internal Bitrix24 pages (profiles, documents, files) → slider
 * - Bitrix24 folders → new tab (slider shows overlay instead of full page)
 * - External URLs → new tab
 */
const shouldUseSlider = (url) => {
  try {
    const u = new URL(url);
    if (!u.hostname.includes('bitrix24.ru')) return false;

    const path = u.pathname;

    // Folders should NOT use slider (they render as overlay instead of full page)
    if (/\/disk\/path\//.test(path)) return false;

    // Employee profiles, documents, files — use slider
    if (/\/company\/personal\/user\//.test(path)) return true;  // employee profile
    if (/\/docs\//.test(path)) return true;                      // documents
    if (/\/disk\//.test(path)) return true;                      // disk files

    // Other internal Bitrix pages — use slider
    return true;
  } catch {
    return false;
  }
};

/**
 * Opens a URL using the best available method:
 * - Desktop Bitrix24 + document link: SDK slider (side panel inside Bitrix24)
 * - Desktop Bitrix24 + folder/external: window.open (new tab)
 * - Mobile Bitrix24: <a target="_blank"> click (opens in-app browser)
 *
 * @param {string} url - URL to open
 */
export const openBitrixLink = (url) => {
  if (!url) return;

  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  if (isMobile) {
    // Bitrix24 mobile does not support slider or openPath.
    // Synchronous <a> click preserves user-gesture context and opens reliably.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  // Desktop: use SDK slider for internal Bitrix24 pages (profiles, docs, files)
  const b24 = getB24();
  if (b24?.slider && shouldUseSlider(url)) {
    try {
      b24.slider.openPath(new URL(url)).catch((err) => {
        console.warn('slider.openPath failed, fallback to window.open:', err);
        window.open(url, '_blank');
      });
      return;
    } catch (e) {
      console.warn('openPath error, fallback:', e);
    }
  }

  // Everything else (folders, external links, dev mode): open in new tab
  window.open(url, '_blank');
};

/**
 * Opens a URL by replacing current location (works best for mobile)
 * @param {string} url - URL to open
 */
export const navigateToUrl = (url) => {
  if (!url) return;
  window.location.href = url;
};
